/* @jsx h */
import { defineComponent, h, Ref } from "vue";
import { Colleague, TableStatus } from "@lemmonhq/types";
import { sortTables } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";

type OrderFilters = {
  isOrderSwapped: Ref<boolean>;
  tables: TableStatus[];
  staff: Colleague[];
  onFilterChange: (filters: {
    status?: string;
    tableId?: number;
    serviceMethod?: string;
    productType?: string;
    staffId?: number;
  }) => void;
  initialFilters: {
    status?: string;
    tableId?: number;
    serviceMethod?: string;
    productType?: string;
    staffId?: number;
  };
};

export const OrderFilters = defineComponent<OrderFilters>({
  name: "OrderFilters",
  props: [
    "tables",
    "staff",
    "onFilterChange",
    "initialFilters",
    "isOrderSwapped",
  ],
  setup(props) {
    const { t } = useLocalization();
    const statuses = [
      { value: "new", label: t("kds.status.new") },
      { value: "preparing", label: t("kds.status.preparing") },
      { value: "ready", label: t("kds.status.ready") },
    ];

    const serviceMethods = [
      { value: "all", label: t("kds.service-type.all") },
      { value: "dine-in", label: t("kds.service-type.dine-in") },
      { value: "takeaway", label: t("kds.service-type.takeaway") },
      { value: "delivery", label: t("kds.service-type.delivery") },
    ];

    const productTypes = [
      { value: "all", label: t("kds.product-type.all") },
      { value: "restaurant", label: t("kds.product-type.restaurant") },
      { value: "bar", label: t("kds.product-type.bar") },
    ];

    const tableOptions = sortTables(props.tables).map((t) => ({
      value: t.table.id,
      label: `${t.table.name}`,
      is_busy: t.table.is_busy,
    }));

    const handleFilterChange = (
      type: string,
      value: string | number | undefined,
    ) => {
      const newFilters = { ...props.initialFilters };
      if (type === "status")
        newFilters.status = value === "all" ? undefined : (value as string);
      if (type === "tableId")
        newFilters.tableId = value === "all" ? undefined : Number(value);
      if (type === "serviceMethod")
        newFilters.serviceMethod =
          value === "all" ? undefined : (value as string);
      if (type === "productType")
        newFilters.productType =
          value === "all" ? undefined : (value as string);
      if (type === "staffId")
        newFilters.staffId = value === "all" ? undefined : Number(value);
      props.onFilterChange(newFilters);
    };

    return () => (
      <div class="h-full hidden lg:block">
        <div
          style={"background: transparent"}
          class="border rounded rounded-lg mb-6 flex flex-col space-y-2"
        >
          <div class="text-md border-b bg-[var(--order-filter-header-background)] px-2 py-2 text-accent flex flex-row">
            <span class="flex justify-start items-center flex-1">
              <span class="material-symbols-outlined mr-1 text-2xl">
                filter_list
              </span>
              {t("orders.filter.filters")}
            </span>
            <div>
              <button
                class={`btn btn-sm ${
                  props.isOrderSwapped.value ? "btn-accent" : "btn-neutral"
                }`}
                onClick={() => {
                  props.isOrderSwapped.value = !props.isOrderSwapped.value;
                }}
              >
                <span class="material-symbols-outlined mr-1 text-2xl">
                  swap_horiz
                </span>
              </button>
            </div>
          </div>
          <div class="px-2 pb-4 flex flex-col gap-4">
            <label class="form-control w-full">
              <div class="label uppercase text-accent">
                <span class="flex justify-start items-center label-text">
                  <span class="material-symbols-outlined mr-1 text-2xl">
                    table_restaurant
                  </span>
                  {t("kds.filters.table")}
                </span>
              </div>
              <select
                class="select select-bordered w-full bg-[var(--order-filter-control-background)] text-accent"
                value={props.initialFilters.tableId || "all"}
                onChange={(e) =>
                  handleFilterChange(
                    "tableId",
                    (e.target as HTMLSelectElement).value,
                  )
                }
              >
                <option value="all">{t("orders.filter.all_tables")}</option>
                {tableOptions.map((table) => (
                  <option key={table.value} value={table.value}>
                    {table.label}
                    {table.is_busy ? ` | ${t("orders.filter.busy")}` : ""}
                  </option>
                ))}
              </select>
            </label>

            <label class="form-control w-full">
              <div class="label uppercase text-accent">
                <span class="flex justify-start items-center label-text">
                  <span class="material-symbols-outlined mr-1 text-2xl">
                    group
                  </span>
                  {t("kds.filters.staff")}
                </span>
              </div>
              <select
                class="select select-bordered w-full bg-[var(--order-filter-control-background)] text-accent"
                value={props.initialFilters.staffId || "all"}
                onChange={(e) =>
                  handleFilterChange(
                    "staffId",
                    (e.target as HTMLSelectElement).value,
                  )
                }
              >
                <option value="all">{t("orders.filter.all_staff")}</option>
                {props.staff.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.profile.name}
                  </option>
                ))}
              </select>
            </label>

            <label class="form-control w-full">
              <div class="label uppercase text-accent">
                <span class="flex justify-start items-center label-text">
                  <span class="material-symbols-outlined mr-1 text-2xl">
                    arrow_upload_progress
                  </span>
                  {t("kds.filters.status")}
                </span>
              </div>
              <select
                class="select select-bordered w-full bg-[var(--order-filter-control-background)] text-accent"
                value={props.initialFilters.status || "all"}
                onChange={(e) =>
                  handleFilterChange(
                    "status",
                    (e.target as HTMLSelectElement).value,
                  )
                }
              >
                <option value="all">{t("orders.filter.all_statuses")}</option>
                {statuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </label>

            <label class="form-control w-full">
              <div class="label uppercase text-accent">
                <span class="flex justify-start items-center label-text">
                  <span class="material-symbols-outlined mr-1 text-2xl">
                    moped
                  </span>
                  {t("kds.filters.service_type")}
                </span>
              </div>
              <select
                class="select select-bordered w-full bg-[var(--order-filter-control-background)] text-accent"
                value={props.initialFilters.serviceMethod || "all"}
                onChange={(e) =>
                  handleFilterChange(
                    "serviceMethod",
                    (e.target as HTMLSelectElement).value,
                  )
                }
              >
                {serviceMethods.map((method) => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </label>

            <label class="form-control w-full">
              <div class="label uppercase text-accent">
                <span class="flex justify-start items-center label-text">
                  <span class="material-symbols-outlined mr-1 text-2xl">
                    category
                  </span>
                  {t("kds.filters.product_type")}
                </span>
              </div>
              <select
                class="select select-bordered w-full bg-[var(--order-filter-control-background)] text-accent"
                value={props.initialFilters.productType || "all"}
                onChange={(e) =>
                  handleFilterChange(
                    "productType",
                    (e.target as HTMLSelectElement).value,
                  )
                }
              >
                {productTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </div>
    );
  },
});
